import { useId, useState } from 'react';
import { X } from 'lucide-react';

/** Free-form tags (Enter or comma adds). Suggestions are one-click shortcuts. */
export function TagsInput({ value, onChange, suggestions = [], placeholder, label, validate }: { value: string[]; onChange: (v: string[]) => void; suggestions?: string[]; placeholder?: string; label: string; validate?: (tag: string) => string | null }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const id = useId();

  const add = (raw: string) => {
    const tags = raw
      .split(/[,\s]+/)
      .map((t) => t.trim())
      .filter(Boolean);
    const next = [...value];
    for (const t of tags) {
      const problem = validate?.(t) ?? null;
      if (problem) {
        setError(problem);
        return;
      }
      if (!next.includes(t)) next.push(t);
    }
    setError(null);
    onChange(next);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" aria-live="polite">
        {value.map((t) => (
          <span key={t} className="badge animate-pop gap-1 pr-1 font-mono badge-soft badge-secondary">
            {t}
            <button type="button" className="dr-focus grid size-4 cursor-pointer place-items-center rounded-full opacity-70 transition-opacity hover:bg-base-content/10 hover:opacity-100" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))}>
              <X className="size-3" aria-hidden="true" />
            </button>
          </span>
        ))}
        {value.length === 0 ? <span className="text-sm text-base-content/55">None selected</span> : null}
      </div>
      <input
        id={id}
        aria-label={label}
        className={`input w-full ${error ? 'input-error' : ''}`}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
            e.preventDefault();
            add(draft);
          } else if (e.key === 'Backspace' && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={() => draft.trim() && add(draft)}
      />
      {error ? (
        <p role="alert" className="text-sm dr-ink-error">
          {error}
        </p>
      ) : null}
      {suggestions.filter((s) => !value.includes(s)).length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-sm">
          <span className="text-base-content/55">Add:</span>
          {suggestions
            .filter((s) => !value.includes(s))
            .map((s) => (
              <button key={s} type="button" className="btn btn-xs rounded-full border-dashed border-hairline font-mono btn-ghost" onClick={() => add(s)}>
                + {s}
              </button>
            ))}
        </div>
      ) : null}
    </div>
  );
}
